import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Divider,
  Menu,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  getExerciseDisplay,
  getExercisesFromEntryWithSection,
  shouldSkipEntry,
  WorkoutAlternateGroup,
  WorkoutExercise,
  WorkoutSuperset,
} from '@strength/domain-workout';
import { getNormalizedSortOrders } from '@strength/utils';
import {
  MdArrowForward,
  MdCheck,
  MdChevronLeft,
  MdEdit,
  MdHistory,
  MdMoreVert,
  MdNoteAdd,
} from '@teamworksdev/icons';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BenchmarkBadge } from '../../../../components/badge/BenchmarkBadge';
import { WorkoutStatusBadge } from '../../../../components/badge/workout-status-badge';
import { cn } from '../../../../components/utils';
import {
  getBenchmarkBadgeGridName,
  hasPercentageIntensity,
} from '../../../../hooks/useBenchmarks';
import { useMetrics } from '../../../../hooks/useMetrics';
import { getWorkoutExerciseFromWorkoutForExercise } from '../../exercise-actuals';
import {
  getEntryKey,
  getMetricsForExerciseDisplay,
  getOriginalSet,
  shouldDisplaySet,
} from '../../utils';
import { useTabletContext } from '../context/TabletContext';
import { useWorkoutContext } from '../context/WorkoutContext';
import { ExerciseSetRow } from './ExerciseSetRow';
import { ExerciseVideoPreview } from './ExerciseVideoPreview';

export const SectionExercises = () => {
  const { onSelectSection, getSelectedSectionId } = useTabletContext();
  const { t } = useTranslation();
  const {
    selectedAthlete,
    workout,
    originalWorkout,
    actualLayer,
    filters,
    hasNextSection,
    handleNextSection,
    handleOpenCompleteWorkoutDialog,
  } = useWorkoutContext();

  const sectionId = getSelectedSectionId(selectedAthlete.id);
  const section = useMemo(() => {
    if (!workout) return undefined;
    return workout.sections.find(
      (section) => section.workoutSectionId === sectionId,
    );
  }, [workout, sectionId]);

  const originalSection = useMemo(() => {
    if (!originalWorkout) return undefined;
    return originalWorkout.sections.find(
      (section) => section.workoutSectionId === sectionId,
    );
  }, [originalWorkout, sectionId]);

  // When the active section is filtered out, navigate to the first remaining visible
  // section so the user isn't stranded on a blank screen.
  useEffect(() => {
    if (!section && filters.exerciseFilters.length > 0 && workout) {
      onSelectSection(workout.sections[0]?.workoutSectionId, selectedAthlete.id);
    }
  }, [section, filters.exerciseFilters.length, workout, onSelectSection, selectedAthlete.id]);

  if (!workout || !originalWorkout) return null;
  if (!section) return null;

  return (
    <div className="flex flex-col grow overflow-hidden">
      <div className="h-[44px] flex bg-gray-50 border-b border-gray-200 justify-between items-center w-full px-1 py-1">
        <Button
          variant="text"
          className="p-2 flex flex-row items-center gap-2"
          onClick={() => {
            onSelectSection(undefined, selectedAthlete.id);
          }}
        >
          <MdChevronLeft size={16} />
          {section.name}
        </Button>
        <Button
          variant="text"
          className="p-2"
          onClick={handleOpenCompleteWorkoutDialog}
        >
          {t('completeWorkout')}
        </Button>
      </div>
      <div className="flex flex-col grow overflow-hidden h-full">
        <div className="relative flex flex-col gap-4 overflow-y-auto h-full pb-20">
          {section.instruction && (
            <div className="flex flex-col pl-6 pt-4 gap-1">
              <Typography variant="body1" className="font-medium">
                {t('instruction')}
              </Typography>
              <Typography variant="body1" className="text-black/60">
                {section.instruction}
              </Typography>
            </div>
          )}

          <div className="flex flex-col">
            <Divider />
            {section.entries.map((entry) => {
              // Skip WorkoutAlternateGroups that overlap with WorkoutSupersets
              if (shouldSkipEntry(entry, section.entries)) {
                return null;
              }

              // Get exercises for this entry, handling superset/alternate group logic
              const exercises = getExercisesFromEntryWithSection(
                entry,
                section,
                actualLayer,
              );

              if (!exercises.length) return null;

              return (
                <div key={getEntryKey(entry)}>
                  {exercises.map((exercise: WorkoutExercise) => {
                    const alternateGroupEntry = section.entries.find(
                      (e) =>
                        e.__type === 'WorkoutAlternateGroup' &&
                        e.exercises.some(
                          (alt) =>
                            alt.workoutExerciseId ===
                            exercise.workoutExerciseId,
                        ),
                    ) as WorkoutAlternateGroup | undefined;

                    return (
                      <ExerciseAccordion
                        key={exercise.workoutExerciseId}
                        exercise={exercise}
                        entry={entry}
                        alternateGroupEntry={alternateGroupEntry}
                      />
                    );
                  })}
                  <Divider />
                </div>
              );
            })}
          </div>
          <Button
            className="fixed bottom-4 right-4 flex flex-row items-center gap-2 bg-white shadow-lg z-10"
            variant="outlined"
            onClick={() => handleNextSection()}
            disabled={!hasNextSection}
          >
            {t('next')}
            <MdArrowForward size={24} />
          </Button>
        </div>
      </div>
    </div>
  );
};

type ExerciseAccordionProps = {
  exercise: WorkoutExercise;
  entry: WorkoutExercise | WorkoutSuperset | WorkoutAlternateGroup;
  // if entry is a WorkoutAlternateGroup, alternateGroupEntry will match the entry
  // alternateGroupEntry is important when a superset contains an alternate group.
  alternateGroupEntry: WorkoutAlternateGroup | undefined;
};

const ExerciseAccordion = ({
  exercise,
  entry,
  alternateGroupEntry,
}: ExerciseAccordionProps) => {
  const { getMetric } = useMetrics();
  const {
    selectedAthlete,
    originalWorkout,
    actualMutationFns,
    exerciseLabelMap,
    exerciseVideoMap,
    context,
    filters,
    applyBenchmarkData,
    getBenchmarkGrid,
    handleOpenAddExerciseDrawer,
    handleOpenAddNoteExerciseDrawer,
    handleEditExerciseStatus,
    handleOpenExerciseHistoryDrawer,
  } = useWorkoutContext();
  const { t } = useTranslation();
  // The athlete's own team default grid id, resolved per-team by WorkoutProvider
  // via the multi-team useTabletBenchmarks hook.
  const { defaultBenchmarkGridId } = applyBenchmarkData;
  const [isExpanded, setIsExpanded] = useState(
    exercise.status !== 'planned' ? false : true,
  );
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

  const isSuperset = entry.__type === 'WorkoutSuperset';
  const isAlternateGroup = !!alternateGroupEntry;

  const displayMetrics = getMetricsForExerciseDisplay(exercise);
  if (!displayMetrics.length) return null;

  const setSortOrders = getNormalizedSortOrders(exercise.sets);

  if (!selectedAthlete || !originalWorkout) return null;

  const originalExercise = getWorkoutExerciseFromWorkoutForExercise(
    originalWorkout,
    exercise.workoutExerciseId,
  );

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setMenuAnchorEl(null);
  };

  const onCompleteExercise = () => {
    const allSetsSkipped = exercise.sets.every(
      (set) => set.status === 'skipped',
    );
    handleEditExerciseStatus(
      exercise.workoutId,
      exercise.workoutExerciseId,
      exercise.sets,
      {
        ...exercise,
        status: allSetsSkipped ? 'skipped' : 'completed',
      },
    );
    setIsExpanded(false);
  };

  const videoUrl = exerciseVideoMap[exercise.libraryExerciseId];

  return (
    <>
      <Accordion
        expanded={isExpanded}
        onChange={(_, isExpanded) => setIsExpanded(isExpanded)}
        disableGutters
        sx={{
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        }}
      >
        <AccordionSummary className="min-h-[66px]">
          <div
            className={cn(
              'flex flex-row justify-between items-center w-full gap-3',
              videoUrl ? 'ml-3' : 'ml-6',
            )}
          >
            {videoUrl && <ExerciseVideoPreview videoUrl={videoUrl} />}
            <div className="flex flex-col w-full items-start">
              <div className="flex flex-row justify-between items-center gap-2">
                <Typography variant="body2" className="font-semibold">
                  {exerciseLabelMap[exercise.workoutExerciseId]}
                </Typography>

                {isAlternateGroup && isExpanded ? (
                  <Select
                    value={alternateGroupEntry.selectedExerciseId}
                    onChange={(e) => {
                      const value = e.target.value;
                      actualMutationFns.onSelectAlternateExercise(
                        context,
                        exercise.workoutId,
                        alternateGroupEntry.workoutAlternateGroupId,
                        value,
                      );
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    {alternateGroupEntry.exercises.map((exercise) => (
                      <MenuItem
                        key={exercise.workoutExerciseId}
                        value={exercise.workoutExerciseId}
                      >
                        {getExerciseDisplay(exercise.exercise, t)}
                      </MenuItem>
                    ))}
                  </Select>
                ) : (
                  <Typography variant="body2Medium">
                    {getExerciseDisplay(
                      {
                        exerciseName: exercise.exerciseName,
                        isEachSide: exercise.isEachSide,
                      },
                      t,
                    )}
                  </Typography>
                )}
                {isSuperset && <SupersetChip />}
                {exercise.status && exercise.status !== 'planned' && (
                  <WorkoutStatusBadge status={{ type: exercise.status }} />
                )}
              </div>
              {exercise.instruction && (
                <Typography
                  variant="caption"
                  className="text-black/60"
                  style={{ whiteSpace: 'pre-wrap' }}
                >
                  {exercise.instruction}
                </Typography>
              )}
            </div>
            {isExpanded && (
              <div className="flex flex-row justify-end items-center gap-2">
                <Button
                  variant="outlined"
                  className="p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCompleteExercise();
                  }}
                >
                  <MdCheck size={24} />
                </Button>
                <Button
                  variant="outlined"
                  className="p-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenMenu(e);
                  }}
                >
                  <MdMoreVert size={24} />
                </Button>
              </div>
            )}
          </div>
        </AccordionSummary>
        <AccordionDetails className="flex flex-col overflow-auto">
          {exercise.sets.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                // +2 for set checkbox and the skip next column
                gridTemplateColumns: `repeat(${
                  displayMetrics.length + 2
                }, minmax(min-content, max-content))`,
                columnGap: 2,
                my: 1,
                ml: 2,
              }}
            >
              {/* place holder for the first column */}
              <div />
              {displayMetrics.map((m) => {
                const percentageIntensity = hasPercentageIntensity(
                  m,
                  exercise.sets.flatMap((s) => s.metrics),
                );
                const benchmarkGridName = getBenchmarkBadgeGridName(
                  m,
                  getBenchmarkGrid,
                  defaultBenchmarkGridId,
                  {
                    libraryExerciseId: exercise.libraryExerciseId,
                    hasPercentageIntensity: percentageIntensity,
                  },
                );
                return (
                  <Typography
                    variant="subtitle1Semibold"
                    className="flex flex-row items-center gap-1"
                    key={`${exercise.workoutExerciseId}-${m.metricName}`}
                  >
                    {getMetric(m.metricId).name}
                    {benchmarkGridName && (
                      <BenchmarkBadge benchmarkGridName={benchmarkGridName} />
                    )}
                  </Typography>
                );
              })}

              {exercise.sets.map((s, index) => {
                // Filter out sets that don't match the set filter
                if (
                  !shouldDisplaySet(
                    s,
                    index,
                    filters.setFilter,
                    exercise.sets.length,
                  )
                ) {
                  return null;
                }

                return (
                  <ExerciseSetRow
                    key={`${exercise.workoutExerciseId}-${s.workoutSetId}`}
                    exercise={exercise}
                    displayMetrics={displayMetrics}
                    set={s}
                    originalSet={getOriginalSet(
                      s.workoutSetId,
                      originalExercise,
                    )}
                    sortOrder={setSortOrders.get(s.sortOrder) ?? 1}
                  />
                );
              })}
            </Box>
          )}
        </AccordionDetails>
      </Accordion>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            marginTop: 1,
            padding: '12px 12px',
            borderRadius: '6px',
            width: '250px',
          },
          '& .MuiList-root': {
            padding: 0,
            '& .MuiMenuItem-root': {
              padding: '8px',
              marginBottom: '4px',
              '&:last-child': {
                marginBottom: 0,
              },
            },
          },
        }}
      >
        <MenuItem
          onClick={() => {
            handleOpenAddNoteExerciseDrawer(exercise);
            handleCloseMenu();
          }}
        >
          <MdNoteAdd
            size={20}
            style={{ marginRight: 8 }}
            className="text-gray-500"
          />
          {t('workoutView.addNotes')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleOpenAddExerciseDrawer(exercise);
            handleCloseMenu();
          }}
        >
          <MdEdit
            size={20}
            style={{ marginRight: 8 }}
            className="text-gray-500"
          />
          {t('editExercise')}
        </MenuItem>
        {
          <MenuItem
            onClick={() => {
              handleOpenExerciseHistoryDrawer(exercise);
              handleCloseMenu();
            }}
          >
            <MdHistory
              size={20}
              style={{ marginRight: 8 }}
              className="text-gray-500"
            />
            {t('exerciseHistory')}
          </MenuItem>
        }
      </Menu>
    </>
  );
};

const SupersetChip = () => {
  const { t } = useTranslation();
  return (
    <Chip
      label={t('superset')}
      className="bg-informational-25 text-informational-700 px-1 text-[12px] h-4"
      sx={{
        '.MuiChip-label': {
          py: 1,
          px: 1,
        },
      }}
    />
  );
};
